import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Upload, X, Save } from 'lucide-react';

interface LushReportData {
  location: string;
  duration: string;
  product: string;
  heroImages: string[];
  footfallMonthly: number;
  footfallTotal: number;
  screenImpressionsMonthly: number;
  screenImpressionsTotal: number;
  adPlayouts: string;
  interactionsMonthly: number;
  interactionsTotal: number;
  browsingSessionsMonthly: number;
  browsingSessionsTotal: number;
  averageBrowseTime: string;
  footfallToBrowseRate: string;
  conversionFootfallToPurchase: string;
  conversionBrowsingToPurchase: string;
  averageSellingPrice: string;
  monthlyUnitsSold: number;
  totalUnitsSold: number;
  initialBoxRevenueMonthly: string;
  initialBoxRevenueTotal: string;
  subscriptionRevenueMonthly: string;
  subscriptionRevenueTotal: string;
  totalCampaignRevenueMonthly: string;
  totalCampaignRevenueTotal: string;
  totalTransactionValue: string;
  threeMonthCompletionRate: string;
  churnRate: string;
  autoRenewTakeUp: string;
  averageLTV: string;
  footfallUplift: string;
  giftCardRedemption: string;
  sameDayCrossPurchase: string;
  incrementalStoreRevenue: string;
  sales10am1pm: number;
  sales1pm4pm: number;
  sales4pm7pm: number;
  sales7pm9pm: number;
  overallExperienceScore: string;
  easeOfPurchaseScore: string;
  giftAppealRating: string;
  wouldRecommend: string;
  feedbackTheme1: string;
  feedbackTheme2: string;
  feedbackTheme3: string;
  strategicTakeaway1: string;
  strategicTakeaway2: string;
  strategicTakeaway3: string;
  strategicTakeaway4: string;
  strategicTakeaway5: string;
  strategicTakeaway6: string;
}

const defaultData: LushReportData = {
  location: 'Coal Drops Yard, King\'s Cross, London',
  duration: '6 Months',
  product: '£120 Lush Experience Box',
  heroImages: [],
  footfallMonthly: 620000,
  footfallTotal: 3720000,
  screenImpressionsMonthly: 1850000,
  screenImpressionsTotal: 11100000,
  adPlayouts: '6 ads rotating',
  interactionsMonthly: 21700,
  interactionsTotal: 130200,
  browsingSessionsMonthly: 16900,
  browsingSessionsTotal: 101400,
  averageBrowseTime: '52 seconds',
  footfallToBrowseRate: '2.7%',
  conversionFootfallToPurchase: '0.42%',
  conversionBrowsingToPurchase: '15.4%',
  averageSellingPrice: '£120',
  monthlyUnitsSold: 2604,
  totalUnitsSold: 15624,
  initialBoxRevenueMonthly: '£312,480',
  initialBoxRevenueTotal: '£1,874,880',
  subscriptionRevenueMonthly: '£174,000',
  subscriptionRevenueTotal: '£696,000',
  totalCampaignRevenueMonthly: '£486,480',
  totalCampaignRevenueTotal: '£2,570,880',
  totalTransactionValue: '£2.57M',
  threeMonthCompletionRate: '81%',
  churnRate: '19%',
  autoRenewTakeUp: '64% of retained base',
  averageLTV: '£186',
  footfallUplift: '+11% vs prior 6-month baseline',
  giftCardRedemption: '23% of machine buyers',
  sameDayCrossPurchase: '18%',
  incrementalStoreRevenue: '£420,000',
  sales10am1pm: 14,
  sales1pm4pm: 26,
  sales4pm7pm: 41,
  sales7pm9pm: 19,
  overallExperienceScore: '4.7 / 5',
  easeOfPurchaseScore: '4.8 / 5',
  giftAppealRating: '4.9 / 5',
  wouldRecommend: '92%',
  feedbackTheme1: 'Best last-minute premium gift solution.',
  feedbackTheme2: 'Loved choosing scent in Month 2.',
  feedbackTheme3: 'More exciting than ordering online.',
  strategicTakeaway1: 'Premium £120 experiential product performs strongly in high-dwell retail environment.',
  strategicTakeaway2: 'Strong subscription retention (81% completion rate).',
  strategicTakeaway3: 'Machine drove measurable store halo effect (+11% uplift).',
  strategicTakeaway4: '15%+ browse-to-purchase conversion.',
  strategicTakeaway5: '£2.57M revenue generated from a single unit over 6 months.',
  strategicTakeaway6: 'Proven model for experiential retail + subscription hybrid commerce.',
};

export default function LushReportEditor() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LushReportData>(defaultData);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Load saved data from localStorage
    try {
      const saved = localStorage.getItem('lush-report-data');
      if (saved) {
        const parsed = JSON.parse(saved);
        setFormData({ ...defaultData, ...parsed });
      }
    } catch (e) {
      console.error('Failed to load saved data:', e);
    }
  }, []);

  async function handleImageUpload(files: File[]) {
    setUploadingImages(true);
    try {
      const uploadedUrls: string[] = [];
      
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          alert(`File ${file.name} is too large. Maximum size is 5MB.`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);
        
        // Try to upload to server
        try {
          const res = await fetch('/api/uploads', {
            method: 'POST',
            credentials: 'include',
            body: formData,
          });
          
          if (res.ok) {
            const data = await res.json();
            uploadedUrls.push(data.url || data.path || URL.createObjectURL(file));
          } else {
            // Fallback to object URL if upload fails
            uploadedUrls.push(URL.createObjectURL(file));
          }
        } catch (e) {
          // Fallback to object URL if upload fails
          uploadedUrls.push(URL.createObjectURL(file));
        }
      }

      setFormData(prev => ({
        ...prev,
        heroImages: [...(prev.heroImages || []), ...uploadedUrls],
      }));
    } catch (error) {
      console.error('Failed to upload images:', error);
      alert('Failed to upload images. Please try again.');
    } finally {
      setUploadingImages(false);
    }
  }

  function handleSave() {
    setSaving(true);
    try {
      localStorage.setItem('lush-report-data', JSON.stringify(formData));
      alert('Report saved successfully!');
      navigate('/reports/lush-experience-box');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save report. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/reports/lush-experience-box')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Report
            </Button>
            <div>
              <h1 className="text-2xl font-light tracking-tight text-slate-900">Edit Lush Report</h1>
              <p className="text-xs text-slate-500 mt-0.5">Modify report content and images</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Report'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="duration">Duration</Label>
                <Input
                  id="duration"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="product">Product</Label>
                <Input
                  id="product"
                  value={formData.product}
                  onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Hero Images */}
          <Card>
            <CardHeader>
              <CardTitle>Hero Images</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const files = Array.from(e.dataTransfer.files).filter(
                    file => file.type.startsWith('image/')
                  );
                  await handleImageUpload(files);
                }}
                className={`
                  border-2 border-dashed rounded-lg p-8 text-center transition-colors
                  ${isDragging ? 'border-slate-400 bg-slate-50' : 'border-slate-300'}
                  ${uploadingImages ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
                `}
                onClick={() => document.getElementById('hero-image-input')?.click()}
              >
                <Upload className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p className="text-lg font-medium mb-2 text-slate-700">
                  Drag and drop images here, or click to select
                </p>
                <p className="text-sm text-slate-500 mb-4">
                  Supports multiple images (max 5MB each)
                </p>
                <Input
                  id="hero-image-input"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      handleImageUpload(Array.from(e.target.files));
                    }
                  }}
                  className="hidden"
                />
              </div>

              {/* Image previews */}
              {formData.heroImages && formData.heroImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {formData.heroImages.map((url, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-video rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                        <img
                          src={url}
                          alt={`Hero ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23f1f5f9"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%2394a3b8"%3EImage%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6"
                        onClick={() => {
                          const newImages = formData.heroImages?.filter((_, i) => i !== index) || [];
                          setFormData({ ...formData, heroImages: newImages });
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Footfall & Visibility */}
          <Card>
            <CardHeader>
              <CardTitle>Footfall & Visibility</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="footfallMonthly">Footfall (Monthly)</Label>
                  <Input
                    id="footfallMonthly"
                    type="number"
                    value={formData.footfallMonthly}
                    onChange={(e) => setFormData({ ...formData, footfallMonthly: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="footfallTotal">Footfall (6-Month Total)</Label>
                  <Input
                    id="footfallTotal"
                    type="number"
                    value={formData.footfallTotal}
                    onChange={(e) => setFormData({ ...formData, footfallTotal: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="screenImpressionsMonthly">Screen Impressions (Monthly)</Label>
                  <Input
                    id="screenImpressionsMonthly"
                    type="number"
                    value={formData.screenImpressionsMonthly}
                    onChange={(e) => setFormData({ ...formData, screenImpressionsMonthly: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="screenImpressionsTotal">Screen Impressions (6-Month Total)</Label>
                  <Input
                    id="screenImpressionsTotal"
                    type="number"
                    value={formData.screenImpressionsTotal}
                    onChange={(e) => setFormData({ ...formData, screenImpressionsTotal: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="adPlayouts">Ad Playouts</Label>
                  <Input
                    id="adPlayouts"
                    value={formData.adPlayouts}
                    onChange={(e) => setFormData({ ...formData, adPlayouts: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Engagement Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Engagement Metrics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="interactionsMonthly">Interactions (Monthly)</Label>
                  <Input
                    id="interactionsMonthly"
                    type="number"
                    value={formData.interactionsMonthly}
                    onChange={(e) => setFormData({ ...formData, interactionsMonthly: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="interactionsTotal">Interactions (6-Month Total)</Label>
                  <Input
                    id="interactionsTotal"
                    type="number"
                    value={formData.interactionsTotal}
                    onChange={(e) => setFormData({ ...formData, interactionsTotal: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="browsingSessionsMonthly">Browsing Sessions (Monthly)</Label>
                  <Input
                    id="browsingSessionsMonthly"
                    type="number"
                    value={formData.browsingSessionsMonthly}
                    onChange={(e) => setFormData({ ...formData, browsingSessionsMonthly: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="browsingSessionsTotal">Browsing Sessions (6-Month Total)</Label>
                  <Input
                    id="browsingSessionsTotal"
                    type="number"
                    value={formData.browsingSessionsTotal}
                    onChange={(e) => setFormData({ ...formData, browsingSessionsTotal: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="averageBrowseTime">Average Browse Time</Label>
                  <Input
                    id="averageBrowseTime"
                    value={formData.averageBrowseTime}
                    onChange={(e) => setFormData({ ...formData, averageBrowseTime: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="footfallToBrowseRate">Footfall → Browse Rate</Label>
                  <Input
                    id="footfallToBrowseRate"
                    value={formData.footfallToBrowseRate}
                    onChange={(e) => setFormData({ ...formData, footfallToBrowseRate: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conversion Performance */}
          <Card>
            <CardHeader>
              <CardTitle>Conversion Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="conversionFootfallToPurchase">Conversion: Footfall → Purchase</Label>
                  <Input
                    id="conversionFootfallToPurchase"
                    value={formData.conversionFootfallToPurchase}
                    onChange={(e) => setFormData({ ...formData, conversionFootfallToPurchase: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="conversionBrowsingToPurchase">Conversion: Browsing → Purchase</Label>
                  <Input
                    id="conversionBrowsingToPurchase"
                    value={formData.conversionBrowsingToPurchase}
                    onChange={(e) => setFormData({ ...formData, conversionBrowsingToPurchase: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="averageSellingPrice">Average Selling Price</Label>
                  <Input
                    id="averageSellingPrice"
                    value={formData.averageSellingPrice}
                    onChange={(e) => setFormData({ ...formData, averageSellingPrice: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="monthlyUnitsSold">Monthly Units Sold</Label>
                  <Input
                    id="monthlyUnitsSold"
                    type="number"
                    value={formData.monthlyUnitsSold}
                    onChange={(e) => setFormData({ ...formData, monthlyUnitsSold: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="totalUnitsSold">Total Units (6 months)</Label>
                  <Input
                    id="totalUnitsSold"
                    type="number"
                    value={formData.totalUnitsSold}
                    onChange={(e) => setFormData({ ...formData, totalUnitsSold: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Performance */}
          <Card>
            <CardHeader>
              <CardTitle>Revenue Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="initialBoxRevenueMonthly">Initial Box Revenue (Monthly)</Label>
                  <Input
                    id="initialBoxRevenueMonthly"
                    value={formData.initialBoxRevenueMonthly}
                    onChange={(e) => setFormData({ ...formData, initialBoxRevenueMonthly: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="initialBoxRevenueTotal">Initial Box Revenue (6-Month Total)</Label>
                  <Input
                    id="initialBoxRevenueTotal"
                    value={formData.initialBoxRevenueTotal}
                    onChange={(e) => setFormData({ ...formData, initialBoxRevenueTotal: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="subscriptionRevenueMonthly">Subscription Revenue (Monthly)</Label>
                  <Input
                    id="subscriptionRevenueMonthly"
                    value={formData.subscriptionRevenueMonthly}
                    onChange={(e) => setFormData({ ...formData, subscriptionRevenueMonthly: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="subscriptionRevenueTotal">Subscription Revenue (6-Month Total)</Label>
                  <Input
                    id="subscriptionRevenueTotal"
                    value={formData.subscriptionRevenueTotal}
                    onChange={(e) => setFormData({ ...formData, subscriptionRevenueTotal: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="totalCampaignRevenueMonthly">Total Campaign Revenue (Monthly)</Label>
                  <Input
                    id="totalCampaignRevenueMonthly"
                    value={formData.totalCampaignRevenueMonthly}
                    onChange={(e) => setFormData({ ...formData, totalCampaignRevenueMonthly: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="totalCampaignRevenueTotal">Total Campaign Revenue (6-Month Total)</Label>
                  <Input
                    id="totalCampaignRevenueTotal"
                    value={formData.totalCampaignRevenueTotal}
                    onChange={(e) => setFormData({ ...formData, totalCampaignRevenueTotal: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="totalTransactionValue">Total Transaction Value Processed</Label>
                  <Input
                    id="totalTransactionValue"
                    value={formData.totalTransactionValue}
                    onChange={(e) => setFormData({ ...formData, totalTransactionValue: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Retention & Churn */}
          <Card>
            <CardHeader>
              <CardTitle>Retention & Churn</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="threeMonthCompletionRate">3-Month Completion Rate</Label>
                  <Input
                    id="threeMonthCompletionRate"
                    value={formData.threeMonthCompletionRate}
                    onChange={(e) => setFormData({ ...formData, threeMonthCompletionRate: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="churnRate">Churn Rate</Label>
                  <Input
                    id="churnRate"
                    value={formData.churnRate}
                    onChange={(e) => setFormData({ ...formData, churnRate: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="autoRenewTakeUp">Auto-Renew Take-Up (Month 4+)</Label>
                  <Input
                    id="autoRenewTakeUp"
                    value={formData.autoRenewTakeUp}
                    onChange={(e) => setFormData({ ...formData, autoRenewTakeUp: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="averageLTV">Average Lifetime Value per Customer</Label>
                  <Input
                    id="averageLTV"
                    value={formData.averageLTV}
                    onChange={(e) => setFormData({ ...formData, averageLTV: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Store Halo Effect */}
          <Card>
            <CardHeader>
              <CardTitle>Store Halo Effect (King's Cross Store)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="footfallUplift">Footfall Uplift</Label>
                  <Input
                    id="footfallUplift"
                    value={formData.footfallUplift}
                    onChange={(e) => setFormData({ ...formData, footfallUplift: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="giftCardRedemption">Gift Card Redemption In-Store</Label>
                  <Input
                    id="giftCardRedemption"
                    value={formData.giftCardRedemption}
                    onChange={(e) => setFormData({ ...formData, giftCardRedemption: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="sameDayCrossPurchase">Same-Day Cross Purchase</Label>
                  <Input
                    id="sameDayCrossPurchase"
                    value={formData.sameDayCrossPurchase}
                    onChange={(e) => setFormData({ ...formData, sameDayCrossPurchase: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="incrementalStoreRevenue">Estimated Incremental Store Revenue</Label>
                  <Input
                    id="incrementalStoreRevenue"
                    value={formData.incrementalStoreRevenue}
                    onChange={(e) => setFormData({ ...formData, incrementalStoreRevenue: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Time of Day Sales */}
          <Card>
            <CardHeader>
              <CardTitle>Sales by Time of Day (%)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sales10am1pm">10am - 1pm</Label>
                  <Input
                    id="sales10am1pm"
                    type="number"
                    value={formData.sales10am1pm}
                    onChange={(e) => setFormData({ ...formData, sales10am1pm: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="sales1pm4pm">1pm - 4pm</Label>
                  <Input
                    id="sales1pm4pm"
                    type="number"
                    value={formData.sales1pm4pm}
                    onChange={(e) => setFormData({ ...formData, sales1pm4pm: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="sales4pm7pm">4pm - 7pm</Label>
                  <Input
                    id="sales4pm7pm"
                    type="number"
                    value={formData.sales4pm7pm}
                    onChange={(e) => setFormData({ ...formData, sales4pm7pm: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="sales7pm9pm">7pm - 9pm</Label>
                  <Input
                    id="sales7pm9pm"
                    type="number"
                    value={formData.sales7pm9pm}
                    onChange={(e) => setFormData({ ...formData, sales7pm9pm: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Feedback */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Feedback</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="overallExperienceScore">Overall Experience Score</Label>
                  <Input
                    id="overallExperienceScore"
                    value={formData.overallExperienceScore}
                    onChange={(e) => setFormData({ ...formData, overallExperienceScore: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="easeOfPurchaseScore">Ease of Purchase</Label>
                  <Input
                    id="easeOfPurchaseScore"
                    value={formData.easeOfPurchaseScore}
                    onChange={(e) => setFormData({ ...formData, easeOfPurchaseScore: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="giftAppealRating">Gift Appeal Rating</Label>
                  <Input
                    id="giftAppealRating"
                    value={formData.giftAppealRating}
                    onChange={(e) => setFormData({ ...formData, giftAppealRating: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="wouldRecommend">Would Recommend</Label>
                  <Input
                    id="wouldRecommend"
                    value={formData.wouldRecommend}
                    onChange={(e) => setFormData({ ...formData, wouldRecommend: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="feedbackTheme1">Feedback Theme 1</Label>
                <Textarea
                  id="feedbackTheme1"
                  value={formData.feedbackTheme1}
                  onChange={(e) => setFormData({ ...formData, feedbackTheme1: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="feedbackTheme2">Feedback Theme 2</Label>
                <Textarea
                  id="feedbackTheme2"
                  value={formData.feedbackTheme2}
                  onChange={(e) => setFormData({ ...formData, feedbackTheme2: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="feedbackTheme3">Feedback Theme 3</Label>
                <Textarea
                  id="feedbackTheme3"
                  value={formData.feedbackTheme3}
                  onChange={(e) => setFormData({ ...formData, feedbackTheme3: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Strategic Takeaways */}
          <Card>
            <CardHeader>
              <CardTitle>Strategic Takeaways</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <div key={num}>
                  <Label htmlFor={`takeaway${num}`}>Takeaway {num}</Label>
                  <Textarea
                    id={`takeaway${num}`}
                    value={formData[`strategicTakeaway${num}` as keyof LushReportData] as string}
                    onChange={(e) => setFormData({ ...formData, [`strategicTakeaway${num}`]: e.target.value } as any)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Save Button at Bottom */}
        <div className="mt-8 flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="lg" className="gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Report'}
          </Button>
        </div>
      </div>
    </div>
  );
}
